<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    :fullscreen="dialogSettings.fullscreen"
    :scrollable="dialogSettings.scrollable"
    :hide-overlay="dialogSettings.hideoverlay"
    :transition="dialogSettings.transition"
    :width="dialogSettings.width"
    :max-width="dialogSettings.maxwidth"
  >
    <v-card class="conv-card">
      <!-- Mobile toolbar -->
      <v-toolbar flat light color="white" class="conv-toolbar" v-if="dialogSettings.fullscreen">
        <v-toolbar-title>{{ $t('messenger.activeConversationTitle') }} {{ conversationStartDate | formatDate }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="$emit('input', false)">
          <v-icon>fa-times</v-icon>
        </v-btn>
      </v-toolbar>
      <v-divider v-if="dialogSettings.fullscreen"></v-divider>

      <!-- Desktop: close + title -->
      <div v-if="!dialogSettings.fullscreen" class="conv-header">
        <div class="d-flex justify-end px-4 pt-2">
          <v-btn color="color-grey" depressed text small @click="$emit('input', false)">
            <v-icon class="mr-3">fa-times</v-icon>
            <span>{{ $t('close') }}</span>
          </v-btn>
        </div>
        <div class="text-center px-4 pb-3">
          <h2 class="text-h2 mt-6 mb-2">{{ $t('messenger.activeConversationTitle') }} {{ conversationStartDate | formatDate }}</h2>
        </div>
      </div>

      <!-- Auth error -->
      <v-card-text v-if="authError" class="messenger-state-box">
        <v-icon color="error" class="mb-3">fas fa-exclamation-triangle</v-icon>
        <p class="text-body-2 mb-3">{{ $t('messenger.authError') }}</p>
        <v-btn tile small outlined color="error" @click="reloadPage">
          {{ $t('messenger.authErrorReload') }}
        </v-btn>
      </v-card-text>

      <!-- Loading -->
      <v-card-text v-else-if="!connected" class="messenger-state-box">
        <v-progress-circular indeterminate color="primary" :size="36" :width="3" />
      </v-card-text>

      <!-- Active chat -->
      <template v-else>
        <!-- Scrollable messages (scrollable:true dialog makes v-card-text fill + scroll) -->
        <v-card-text
          class="conv-messages-area pa-0"
          ref="messageList"
          @dragover.prevent="dragOver = true"
          @dragleave.self="dragOver = false"
          @drop.prevent="onDrop"
          :class="{ 'drag-over': dragOver }"
        >
          <div class="content-wrap">
          <template v-for="item in localMessagesWithDates">
            <div v-if="item.type === 'date-separator'" :key="'date-' + item.id" class="date-separator">
              <span class="date-separator-label">{{ item.date | formatDate }}</span>
            </div>
            <div
              v-else
              :key="'message-' + item.id"
              :class="['message', item.direction]"
            >
              <div class="message-inner">
                <div v-if="item.direction === 'incoming'" class="msg-header incoming-header">
                  <img src="/static/favicons/android-chrome-192x192.png" class="b2-logo" alt="B2 Impact" />
                  <span class="msg-sender">{{ item.isBot ? 'B2 Impact Bot' : 'B2 Impact' }}</span>
                  <span class="msg-time">{{ item.timestamp | formatTime }}</span>
                </div>
                <div v-else class="msg-header outgoing-header">
                  <span class="msg-sender">{{ debtorFirstName }}</span>
                  <span class="msg-time">{{ item.timestamp | formatTime }}</span>
                </div>
                <div :class="['bubble', item.direction === 'outgoing' ? 'outgoing-bubble' : 'incoming-bubble']">
                  <div v-if="item.files && item.files.length" class="bubble-files">
                    <div v-for="file in item.files" :key="file.id" class="bubble-file">
                      <span
                        class="bubble-attachment"
                        :class="{ 'bubble-attachment--loading': downloadingIds[file.id] }"
                        @click="downloadAttachment(file)"
                      >
                        <v-progress-circular
                          v-if="downloadingIds[file.id]"
                          indeterminate
                          :size="14"
                          :width="2"
                          class="mr-1"
                        />
                        <v-icon v-else small class="mr-1">fas fa-paperclip</v-icon>
                        {{ file.name }}
                      </span>
                    </div>
                  </div>
                  <div v-if="item.text" class="bubble-text">{{ item.text }}</div>
                </div>
              </div>
            </div>
          </template>
          <div v-if="agentTyping" class="message incoming">
            <div class="message-inner">
              <div class="msg-header incoming-header">
                <img src="/static/favicons/android-chrome-192x192.png" class="b2-logo" alt="B2 Impact" />
                <span class="msg-sender">B2 Impact</span>
              </div>
              <div class="bubble incoming-bubble typing-bubble">
                <span class="typing-dot"></span>
                <span class="typing-dot"></span>
                <span class="typing-dot"></span>
              </div>
            </div>
          </div>
          <div v-if="sending" class="message outgoing">
            <div class="message-inner">
              <div class="bubble outgoing-bubble">
                <v-progress-circular indeterminate :size="16" :width="2" color="color-white" />
              </div>
            </div>
          </div>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <!-- Staged attachments preview (not yet uploaded) -->
        <div v-if="stagedFiles.length" class="staged-preview">
          <div v-for="staged in stagedFiles" :key="staged.id" class="staged-item">
            <img v-if="staged.isImage" :src="staged.previewUrl" class="staged-thumb" :alt="staged.name" />
            <div v-else class="staged-doc">
              <v-icon small color="color-new-link-blue">fas fa-file</v-icon>
              <span class="staged-doc-name">{{ staged.name }}</span>
            </div>
            <v-btn icon x-small class="staged-remove" :disabled="sending" @click="removeStagedFile(staged.id)">
              <v-icon x-small>fas fa-times</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Attachment queue -->
        <div v-if="pendingAttachments.length" class="attachment-queue">
          <v-progress-linear
            :indeterminate="uploadProgress === null"
            :value="uploadProgress || 0"
            color="primary"
            height="4"
            rounded
            class="attachment-progress"
          ></v-progress-linear>
          <div class="attachment-list">
            <div v-for="att in pendingAttachments" :key="att.id" class="attachment-row">
              <v-icon x-small class="mr-2">fas fa-paperclip</v-icon>
              <span class="attachment-row-name">{{ att.name }}</span>
            </div>
          </div>
        </div>

        <!-- Upload error -->
        <div v-if="uploadError" class="upload-error">
          <v-icon x-small color="error" class="mr-1">fas fa-exclamation-circle</v-icon>
          {{ $t(uploadError) || uploadError }}
        </div>

        <!-- Input pinned at bottom via v-card-actions in scrollable dialog -->
        <v-card-actions class="messenger-input pa-3">
          <v-text-field
            ref="inputField"
            v-model="input"
            outlined
            dense
            rounded
            :placeholder="$t('messenger.inputPlaceholder')"
            :disabled="sending"
            @input="onInput"
            @keyup.enter="submit"
            hide-details
            maxlength="4096"
          >
            <template v-slot:prepend-inner>
              <v-btn icon x-small :disabled="sending || uploadingFiles || !connected" class="mt-1" @click="$refs.fileInput.click()">
                <v-icon small>fas fa-paperclip</v-icon>
              </v-btn>
            </template>
            <template v-slot:append>
              <v-btn
                icon
                color="primary"
                :disabled="(!input && !stagedFiles.length) || sending"
                @click="submit"
              >
                <v-icon>fas fa-paper-plane</v-icon>
              </v-btn>
            </template>
          </v-text-field>

          <!-- Hidden file input for the attach button -->
          <input
            ref="fileInput"
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.txt"
            style="display:none"
            @change="onFilesSelected"
          />
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'

  export default {
    name: 'ConversationModal',

    props: {
      value: {
        type: Boolean,
        default: false
      }
    },

    data: function () {
      return {
        input: '',
        typingInterval: null,
        dragOver: false,
        uploadingFiles: false,
        downloadingIds: {},
        stagedFiles: []
      }
    },

    computed: {
      ...mapState('messenger', ['sending', 'connected', 'agentTyping', 'pendingAttachments', 'uploadError', 'uploadProgress']),
      ...mapGetters('messenger', ['activeConversationWithDates', 'authError']),
      ...mapGetters(['debtor']),
      debtorFirstName: function () {
        return (this.debtor && this.debtor.firstName) || ''
      },
      conversationStartDate: function () {
        var items = this.activeConversationWithDates
        for (var i = 0; i < items.length; i++) {
          if (items[i].type !== 'date-separator') {
            return items[i].timestamp || null
          }
        }
        return null
      },
      localMessagesWithDates: function () {
        var skippedFirst = false
        return this.activeConversationWithDates.filter(function (item) {
          if (!skippedFirst && item.type === 'date-separator') {
            skippedFirst = true
            return false
          }
          return true
        })
      },
      dialogSettings: function () {
        return {
          fullscreen: this.$vuetify.breakpoint.smAndDown,
          scrollable: true,
          hideoverlay: this.$vuetify.breakpoint.smAndDown,
          transition: this.$vuetify.breakpoint.smAndDown ? 'dialog-bottom-transition' : 'dialog-transition',
          width: this.$vuetify.breakpoint.smAndDown ? 'auto' : '860px',
          maxwidth: this.$vuetify.breakpoint.smAndDown ? 'none' : '90%'
        }
      }
    },

    watch: {
      activeConversationWithDates: function () {
        this.scrollToBottom()
      },
      agentTyping: function (val) {
        if (val) this.scrollToBottom()
      },
      value: function (val) {
        // Track open/closed so the store knows whether the user is viewing the
        // conversation. While closed, incoming agent messages raise the
        // "new messages" banner instead of being marked as seen.
        this.$store.commit('messenger/SET_CONVERSATION_OPEN', val)
        if (val) this.scrollToBottom()
      }
    },

    beforeDestroy: function () {
      // Navigated away while the session stays connected in the background —
      // ensure the store no longer considers the conversation open.
      this.$store.commit('messenger/SET_CONVERSATION_OPEN', false)
      this.stopTyping()
      this.clearStagedFiles()
    },

    methods: {
      scrollToBottom: function () {
        var self = this
        self.$nextTick(function () {
          var el = self.$refs.messageList
          if (el) {
            var target = el.$el || el
            target.scrollTop = target.scrollHeight
          }
        })
      },
      toAsciiFileName: function (name) {
        var safe = (name || 'attachment')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^\x20-\x7E]/g, '_')
        if (!safe.trim()) return 'attachment'
        return safe
      },
      onInput: function () {
        if (!this.connected || !this.input) {
          this.stopTyping()
          return
        }
        window.$genesys.sendTyping()
        if (!this.typingInterval) {
          var self = this
          this.typingInterval = setInterval(function () {
            if (self.input && self.connected) {
              window.$genesys.sendTyping()
            } else {
              self.stopTyping()
            }
          }, 1000)
        }
      },
      stopTyping: function () {
        if (this.typingInterval) {
          clearInterval(this.typingInterval)
          this.typingInterval = null
        }
      },
      onFilesSelected: function (event) {
        var files = event.target.files
        if (files && files.length) {
          this.stageFiles(files)
        }
        // Reset so the same file can be picked again
        event.target.value = ''
      },
      onDrop: function (event) {
        this.dragOver = false
        var files = event.dataTransfer && event.dataTransfer.files
        if (files && files.length) {
          this.stageFiles(files)
        }
      },
      // Hold selected files locally with a preview instead of uploading immediately.
      // The actual upload happens on submit, after the optional text message is sent.
      stageFiles: function (files) {
        var list = Array.prototype.slice.call(files || []).filter(function (f) { return f && f.name })
        if (!list.length) return
        this.$store.commit('messenger/CLEAR_UPLOAD_ERROR')
        var self = this
        list.forEach(function (f) {
          var isImage = /^image\//.test(f.type)
          self.stagedFiles.push({
            id: 'staged-' + Date.now() + '-' + Math.random().toString(36).slice(2),
            file: f,
            name: f.name,
            isImage: isImage,
            previewUrl: isImage ? URL.createObjectURL(f) : null
          })
        })
      },
      removeStagedFile: function (id) {
        var idx = this.stagedFiles.findIndex(function (s) { return s.id === id })
        if (idx === -1) return
        if (this.stagedFiles[idx].previewUrl) {
          URL.revokeObjectURL(this.stagedFiles[idx].previewUrl)
        }
        this.stagedFiles.splice(idx, 1)
      },
      clearStagedFiles: function () {
        this.stagedFiles.forEach(function (s) {
          if (s.previewUrl) URL.revokeObjectURL(s.previewUrl)
        })
        this.stagedFiles = []
      },
      submit: async function () {
        if (this.sending) return
        if (!this.input && !this.stagedFiles.length) return
        this.stopTyping()

        // 1. Send the typed text as its own message first (if any). It is intentionally
        //    NOT attached as an image caption — the attachment message text is reused
        //    as the file name when messages are downloaded later.
        if (this.input) {
          var text = this.input
          this.input = ''
          await this.$store.dispatch('messenger/sendMessage', text)
        }

        // 2. Then upload each staged file as its own separate message.
        if (this.stagedFiles.length) {
          var files = this.stagedFiles.map(function (s) { return s.file })
          this.clearStagedFiles()
          this.$store.dispatch('messenger/queueFilesForUpload', files)
        }

        var self = this
        this.$nextTick(function () {
          if (self.$refs.inputField) {
            self.$refs.inputField.focus()
          }
        })
      },
      reloadPage: function () {
        window.location.reload()
      },

      downloadAttachment: function (file) {
        if (this.downloadingIds[file.id]) return
        var self = this
        self.$set(self.downloadingIds, file.id, true)
        window.Genesys(
          'command',
          'MessagingService.downloadFile',
          { downloadUrl: file.url, name: self.toAsciiFileName(file.name) },
          function (data) {
            var blob = data instanceof Blob ? data : (data && (data.blob || data.data))
            self.$set(self.downloadingIds, file.id, false)
            if (!blob) {
              console.error('[ConversationModal] downloadFile: unexpected response', data)
              return
            }
            var blobUrl = URL.createObjectURL(blob)
            var a = document.createElement('a')
            a.href = blobUrl
            a.download = file.name
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(blobUrl)
          },
          function (error) {
            self.$set(self.downloadingIds, file.id, false)
            console.error('[ConversationModal] downloadFile failed', error)
          }
        )
      }
    }
  }
</script>

<style scoped>
  /* Shared conversation dialog styles — mirrors ConversationHistoryDialog */

  .conv-card {
    background: #F7F8FA;
  }

  .conv-toolbar {
    max-height: 56px;
  }

  .conv-header {
    background: #fff;
  }

  .conv-messages-area {
    /* v-card-text default is flex:1 — keep it so input stays pinned at bottom */
    overflow-y: auto;
    background: #F7F8FA;
    padding: 8px 16px 16px;
  }

  .conv-messages-area.drag-over .content-wrap {
    box-shadow: inset 0 0 0 3px #40c6a6;
    background: #edfaf7;
  }

  .content-wrap {
    max-width: 560px;
    margin-left: auto;
    margin-right: auto;
    border-radius: 4px;
    padding: 16px;
  }

  .messenger-state-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    min-height: 300px;
    padding: 24px;
  }

  .messenger-input {
    flex: 0 0 auto;
    background: #fff;
  }

  .messenger-input >>> .v-input__append-inner {
    margin-top: 2px;
  }

  .messenger-input >>> .v-input__prepend-inner {
    margin-top: 2px;
  }

  .staged-preview {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px 12px;
    background: #fff;
    border-top: 1px solid #EEF1F4;
  }

  .staged-item {
    position: relative;
    display: inline-flex;
    align-items: center;
  }

  .staged-thumb {
    width: 64px;
    height: 64px;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid #D8DCE3;
  }

  .staged-doc {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 180px;
    padding: 10px 12px;
    border-radius: 6px;
    border: 1px solid #D8DCE3;
    background: #F7F8FA;
    font-size: 12px;
    color: #3D546C;
  }

  .staged-doc-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .staged-remove {
    position: absolute;
    top: -8px;
    right: -8px;
    background: #fff;
    border: 1px solid #D8DCE3;
  }

  .attachment-queue {
    padding: 8px 12px 6px;
    background: #fff;
    border-top: 1px solid #EEF1F4;
  }

  .attachment-progress {
    margin-bottom: 8px;
  }

  .attachment-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .attachment-row {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    font-size: 12px;
    color: #5A6F86;
  }

  .attachment-row-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .upload-error {
    display: flex;
    align-items: center;
    padding: 4px 12px;
    font-size: 12px;
    color: #e53935;
    background: #fff;
  }

  .message {
    display: flex;
    margin-bottom: 24px;
  }

  .message.incoming {
    justify-content: flex-start;
  }

  .message.outgoing {
    justify-content: flex-end;
  }

  .message-inner {
    display: flex;
    flex-direction: column;
    max-width: 75%;
  }

  .incoming-header {
    justify-content: flex-start;
  }

  .outgoing-header {
    justify-content: flex-end;
  }

  /* B2 logo thumbnail */
  .b2-logo {
    width: 32px;
    height: 32px;
    object-fit: contain;
    border-radius: 4px;
  }

  .msg-sender {
    font-weight: 600;
    /* color-new-dark-grey: #435262 */
    color: #435262;
  }

  .msg-time {
    /* color-grey: #A2ADB8 */
    color: #A2ADB8;
  }

  /* Sender header above bubble */
  .msg-header {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
    font-size: 12px;
  }

  /* Bubble base */
  .bubble {
    padding: 12px 16px;
    border-radius: 10px;
    word-break: break-word;
    line-height: 1.5;
  }

  /* Outgoing bubble — white with primary green border */
  .outgoing-bubble {
    background: #fff;
    border: 2.5px solid #40c6a6;
    /* color-new-body-grey: #3D546C */
    color: #3D546C;
  }

  /* Incoming bubble — color-new-link-blue border: #485cc7 */
  .incoming-bubble {
    background: #fff;
    border: 2.5px solid #485cc7;
    /* color-new-body-grey: #3D546C */
    color: #3D546C;
  }

  .bubble-files {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 4px;
  }

  .bubble-attachment {
    display: inline-flex;
    align-items: center;
    font-size: 13px;
    color: inherit;
    text-decoration: underline;
    word-break: break-all;
    cursor: pointer;
  }

  .bubble-attachment--loading {
    cursor: default;
    opacity: 0.65;
  }

  .bubble-text {
    word-break: break-word;
    line-height: 1.4;
  }

  .date-separator {
    display: flex;
    align-items: center;
    margin: 12px 0;
    gap: 8px;
  }

  .date-separator::before,
  .date-separator::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #D8DCE3;
  }

  .date-separator-label {
    font-size: 11px;
    color: #8A99A8;
    white-space: nowrap;
    padding: 2px 8px;
    background: #F7F8FA;
    border-radius: 10px;
    border: 1px solid #D8DCE3;
  }

  .typing-bubble {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 12px 16px;
    min-width: 52px;
  }

  .typing-dot {
    display: inline-block;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #8A99A8;
    animation: typing-bounce 1.2s infinite ease-in-out;
  }

  .typing-dot:nth-child(1) { animation-delay: 0s; }
  .typing-dot:nth-child(2) { animation-delay: 0.2s; }
  .typing-dot:nth-child(3) { animation-delay: 0.4s; }

  @keyframes typing-bounce {
    0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
    30%            { transform: translateY(-5px); opacity: 1; }
  }
</style>
